import type { Result } from '@empathyco/x-types'
import type { LocaleEnum } from '../enums'
import type { Columns, Metadata } from './csv-generator.utils'
import { CSVGeneratorUtils } from './csv-generator.utils'
import { ExplainCSVExportPinnedProductRow } from './explain-csv-export.utils/explain-csv-export-pinned-product-row'
import { ExplainCSVExportRow } from './explain-csv-export.utils/explain-csv-export-row'
import { ExplainUtils } from './explain.utils'

interface ColumnsOptions {
  textualMatchKeys: Set<string>
  maxDepartments: number
  maxCommodities: number
  maxSubCommodities: number
}

export class ExplainCSVExportUtils {
  /**
   * Generates a CSV Export for a set of Explain results.
   *
   * @param query - Query.
   * @param user - User.
   * @param currentDate - Current date.
   * @param results - An array of explain results.
   * @param messages - i18n messages.
   * @param locale - The locale used for formatting values.
   * @param hasConfidentialData - Show or not columns with scores
   * @returns A string with the CSV export.
   */
  static generateCSV(
    query: string,
    user: string,
    currentDate: string,
    results: Result[],
    messages: Record<string, string>,
    locale: LocaleEnum,
    hasConfidentialData: boolean,
  ): string {
    const { rows, columnOptions } = this.processResults(results)
    const columns = this.getColumns(messages, columnOptions, hasConfidentialData)
    const metadata = this.getMetadata(query, user, currentDate, rows, messages)
    return CSVGeneratorUtils.generateCSV<ExplainCSVExportRow>(metadata, columns, rows, locale)
  }

  private static processResults(results: Result[]): {
    rows: ExplainCSVExportRow[]
    columnOptions: ColumnsOptions
  } {
    const textualMatchKeys = new Set<string>()
    let maxDepartments = 0
    let maxCommodities = 0
    let maxSubCommodities = 0

    const rows: ExplainCSVExportRow[] = []
    results.forEach((result, index) => {
      const RowClass = this.getRowClass(result)
      const row = new RowClass(result, { position: index + 1 })
      rows.push(row)

      row.textualMatchDetails().forEach(textualMatch => {
        textualMatchKeys.add(textualMatch.fieldName)
      })

      maxDepartments = Math.max(result.cfDepartments?.length || 0, maxDepartments)
      maxCommodities = Math.max(result.cfCommodities?.length || 0, maxCommodities)
      maxSubCommodities = Math.max(result.cfSubCommodities?.length || 0, maxSubCommodities)
    })

    return {
      rows,
      columnOptions: { textualMatchKeys, maxDepartments, maxCommodities, maxSubCommodities },
    }
  }

  private static getRowClass(result: Result): typeof ExplainCSVExportRow {
    if (ExplainUtils.isResultPinned(result)) {
      return ExplainCSVExportPinnedProductRow
    }

    return ExplainCSVExportRow
  }

  private static getConfidentialColumns(
    messages: Record<string, string>,
    options: ColumnsOptions,
  ): Columns<ExplainCSVExportRow> {
    return {
      [messages.totalScore]: row => row.totalScore,
      [`${messages.textualMatch} Total`]: row => row.textualMatchTotalScore,
      ...this.getTextualMatchColumns(options, messages),
      [messages.semanticSearchInfo]: row => row.semanticSearchScore,
      [messages.gbrBoost]: row => row.gbrBoostScore,
      [messages.gbrBuries]: row => row.gbrBuriesScore,
      [messages.functionScore]: row => row.functionScore,
      [messages.productBoost]: row => row.productBoostScore,
      [messages.attributeBoost]: row => row.attributeBoostScore,
      [messages.productBury]: row => row.productBuryScore,
      [messages.attributeBury]: row => row.attributeBuryScore,
      [messages.topProduct]: row => row.topProductScore,
      [messages.topAttributes]: row => row.topAttributesScore,
      [messages.termPosition]: row => row.termPositionScore,
      [messages.customization]: row => {
        return row.customizationScore
      },
      [messages.tieBreaker]: row => {
        return row.tieBreaker
      },
    }
  }
  private static getColumns(
    messages: Record<string, string>,
    options: ColumnsOptions,
    hasConfidentialData = true,
  ): Columns<ExplainCSVExportRow> {
    return {
      [messages.position]: row => row.position,
      [messages.product]: row => row.id,
      [messages.description]: row => row.name,
      [messages.brand]: row => row.brand,
      [messages.cfSize]: row => row.cfSize,
      ...this.getCfColumns(options, messages),
      [messages.primaryDepartment]: row => row.primaryDepartments,
      [messages.recapDepartment]: row => row.recapDepartments,
      [messages.ftDepartment]: row => row.ftDepartments,
      [messages.ftCommodity]: row => row.ftCommodities,
      [messages.ftSubCommodity]: row => row.ftSubCommodities,
      ...(hasConfidentialData && { ...this.getConfidentialColumns(messages, options) }),
    }
  }

  private static getCfColumns(
    options: ColumnsOptions,
    messages: Record<string, string>,
  ): Columns<ExplainCSVExportRow> {
    const cfColumns: Columns<ExplainCSVExportRow> = {}
    for (let i = 0; i < options.maxDepartments; i++) {
      cfColumns[`${messages.cfDepartment} ${i + 1}`] = row => row.cfDepartments[i]
    }
    for (let i = 0; i < options.maxCommodities; i++) {
      cfColumns[`${messages.cfCommodity} ${i + 1}`] = row => row.cfCommodities[i]
    }
    for (let i = 0; i < options.maxSubCommodities; i++) {
      cfColumns[`${messages.cfSubCommodity} ${i + 1}`] = row => row.cfSubCommodities[i]
    }
    return cfColumns
  }

  private static getTextualMatchColumns(
    options: ColumnsOptions,
    messages: Record<string, string>,
  ): Columns<ExplainCSVExportRow> {
    const textualMatchColumns: Columns<ExplainCSVExportRow> = {}
    options.textualMatchKeys.forEach(textualMatchKey => {
      textualMatchColumns[`${messages.textualMatch} - ${textualMatchKey}`] = row =>
        row.textualMatchScore(textualMatchKey)
    })
    return textualMatchColumns
  }

  private static getMetadata(
    query: string,
    user: string,
    currentDate: string,
    rows: ExplainCSVExportRow[],
    messages: Record<string, string>,
  ): Metadata {
    return {
      [messages.queryText]: query,
      [messages.dateTime]: currentDate,
      [messages.userId]: user,
      [messages.numberOfProducts]: rows.length,
    }
  }
}
